/**
 * Where the day starts, and how to print it.
 *
 * The position normally comes from the day's program. A product the DMC never
 * distributed to the reader has no programs at all — the API reduces it to its
 * identification (InaccessibleProductResponse) — but that response does carry
 * the experience's own `startPosition`, so the day can still say where it
 * starts instead of showing nothing.
 */
export function getDayStartPosition(program, experience) {
  return program?.startPosition ?? experience?.startPosition;
}

/**
 * "City, Country", dropping whatever is missing: a position with only one of
 * the two, or no position at all, must never print a stray comma or the string
 * "undefined" into the PDF.
 */
export function formatPositionLabel(position) {
  return [position?.city, position?.country].filter(Boolean).join(', ');
}

/**
 * Position of a day inside its experience, as { number, count }.
 *
 * Only needed for a product with no program: every one of its days falls back
 * to the same experience name, so without a number they read as duplicates.
 * `durationDays` is not part of InaccessibleProductResponse, so the total is
 * measured on the trip instead — the run of consecutive days referencing the
 * same experience with a continuous experienceDayIndex. An experience used
 * twice in one trip therefore numbers each stay on its own.
 */
export function getExperienceDayPosition(trip, day) {
  const number = (day?.experienceDayIndex ?? 0) + 1;
  const tripDays = trip?.tripDays ?? [];
  const position = tripDays.findIndex((tripDay) => tripDay?.id === day?.id);

  if (!day?.experienceId || position === -1) {
    return { number, count: 1 };
  }

  const belongsToSameRun = (candidate, neighbour, step) =>
    candidate?.experienceId === day.experienceId &&
    candidate?.experienceDayIndex === neighbour?.experienceDayIndex + step;

  let first = position;
  while (
    first > 0 &&
    belongsToSameRun(tripDays[first - 1], tripDays[first], -1)
  ) {
    first -= 1;
  }

  let last = position;
  while (
    last < tripDays.length - 1 &&
    belongsToSameRun(tripDays[last + 1], tripDays[last], 1)
  ) {
    last += 1;
  }

  return { number, count: last - first + 1 };
}

/**
 * "<Day label> N - <product>", with the product numbered inside its experience
 * when it has no program of its own (see getExperienceDayPosition). Every part
 * is optional: a product stripped of its name just leaves the day number.
 */
export function formatDayTitle({
  dayLabel,
  index,
  program,
  experience,
  trip,
  day,
  separator = '-',
}) {
  const dayNumber = `${dayLabel} ${index + 1}`;
  const productName = program?.title ?? experience?.name;

  if (!productName) {
    return dayNumber;
  }

  // A day with its own program title is already uniquely named; only the
  // fallback to the experience name needs the numbering.
  const { number, count } = program?.title
    ? { number: 1, count: 1 }
    : getExperienceDayPosition(trip, day);

  const label = count > 1 ? `${productName} (${number}/${count})` : productName;

  return `${dayNumber} ${separator} ${label}`;
}
